import { mutatePurchaseOrders } from "@/api/purchaseOrder/usePurchaseOrder";
import { useModal } from "@/features/modal/ModalStackManager";
import { OrderItemsCreateForm } from "@/features/orderItems/form/createForm";
import { FormButtonBox } from "@/features/standard/Form/FormButtonBox";
import { FormMain } from "@/features/standard/Form/FormMain";
import { FormSelect } from "@/features/standard/Form/FormSelect";
import { FormWrapper } from "@/features/standard/Form/FormWrapper";
import customAlert from "@/features/ui/alert/alert";
import { LocationAutoComplete } from "@/features/ui/autoComplete/location/location-autoComplate";
import { PartnerAutoComplete } from "@/features/ui/autoComplete/partner/partner-autoComplete";
import { UserAutoComplete } from "@/features/ui/autoComplete/user/user-autoComplete";
import { ConfirmForm } from "@/features/ui/confirm";
import { getUserCodeByUser } from "@/utils/checkData";
import { handleErrorResponse } from "@/utils/errorMessage";
import { validateFormValues } from "@/utils/validation";
import { Button, Flex, Text, TextInput } from "@mantine/core";
import { DateInput } from "@mantine/dates";
import { useForm } from "@mantine/form";
import { ScmApiPurchaseOrdersPostRequest } from "@sizlcorp/sizl-api-document/dist/models";
import { IconCalendar } from "@tabler/icons-react";
import { useMutation, useQueryClient } from "@tanstack/react-query";

const Create = Object.assign(FormMain, {
  Button: Button,
  Input: TextInput,
  Select: FormSelect,
  Wrapper: FormWrapper,
  ButtonBox: FormButtonBox,
  Text: Text,
  Flex: Flex,
  DateInput: DateInput,
});

export const OrderCreateForm = () => {
  const { openModal, closeModal } = useModal();
  const userCode = getUserCodeByUser();
  const queryClient = useQueryClient();
  const { mutate: createMutate } = useMutation(
    (params: ScmApiPurchaseOrdersPostRequest) =>
      mutatePurchaseOrders
        .create(params)
        .mutationFn(params as ScmApiPurchaseOrdersPostRequest | any),
    {
      onSuccess: (res) => {
        queryClient.invalidateQueries(["purchaseOrders"]);
        closeModal(form.values);
        customAlert(
          `${userCode === "FLOUR" ? "수주 등록에 성공하였습니다." : "발주 등록에 성공하였습니다."}`,
          "등록 성공",
          "green",
        );
        openModal(
          <ConfirmForm
            message={`${userCode === "FLOUR" ? "수주 항목 등록을 하시겠습니까?" : "발주 항목 등록을 하시겠습니까?"}`}
            yesCallback={() => {
              openModal(
                <OrderItemsCreateForm order={res.data} />,
                null,
                `${userCode === "FLOUR" ? "수주 항목 추가" : "발주 항목 추가"}`,
                true,
              ).then(() => closeModal(null));
            }}
            noCallback={() => closeModal(null)}
          />,
          null,
          `${userCode === "FLOUR" ? "수주 항목 추가" : "발주 항목 추가"}`,
          true,
        );
      },
      onError: (error: any) => {
        customAlert(
          handleErrorResponse({ error, errorContext: `${userCode === "FLOUR" ? "수주" : "발주"}` }),
          `${userCode === "FLOUR" ? "수주 등록 실패" : "발주 등록 실패"}`,
          "red",
        );
      },
    },
  );

  const form = useForm({
    initialValues: {
      partnerCode: undefined,
      // status: undefined,
      code: undefined,
      orderDate: undefined,
      deliveryDeadline: undefined,
      userCode: undefined,
      targetLocationCode: undefined,
    },
  });

  const onSubmit = async () => {
    try {
      createMutate({
        purchaseOrdersGetRequest: {
          partnerCode: form.values.partnerCode!,
          direction: "OUTBOUND",
          code: form.values.code!,
          // status: form.values.status!,
          orderDate: form.values.orderDate!,
          deliveryDeadline: form.values.deliveryDeadline!,
          userCode: form.values.userCode!,
          targetLocationCode: form.values.targetLocationCode!,
        },
      });
    } catch (e) {
      customAlert(
        `${userCode === "FLOUR" ? "수주 등록에 실패하였습니다." : "발주 등록에 실패하였습니다."}`,
        "등록 실패",
        "red",
      );
    }
  };

  return (
    <>
      <Create.Wrapper>
        <PartnerAutoComplete
          withAsterisk
          label="협력사"
          placeholder="협력사"
          {...form.getInputProps("partnerCode")}
        />
        <Create.Input
          withAsterisk
          label={`${userCode === "FLOUR" ? "수주 코드" : "발주 코드"}`}
          placeholder={`${userCode === "FLOUR" ? "수주 코드 입력" : "발주 코드 입력"}`}
          {...form.getInputProps("code")}
        />
        {/* <Select
                    label="발주상태"
                    placeholder="발주상태"
                    data={[
                        { value: "DRAFT", label: "초안" },
                        { value: "IN_PROGRESS", label: "진행중" },
                        { value: "DONE", label: "완료" },
                        { value: "CANCELED", label: "취소됨" },
                    ]}
                    {...form.getInputProps("status")}
                /> */}
        <UserAutoComplete
          label="담당자"
          placeholder="담당자 입력"
          {...form.getInputProps("userCode")}
        />
        <LocationAutoComplete
          label="입고 로케이션"
          placeholder="입고 로케이션"
          {...form.getInputProps("targetLocationCode")}
        />
        <Create.DateInput
          popoverProps={{ withinPortal: true }}
          icon={<IconCalendar size="1.2rem" />}
          label="발주일"
          valueFormat="YYYY-MM-DD"
          {...form.getInputProps("orderDate")}
        />
        <Create.DateInput
          popoverProps={{ withinPortal: true }}
          icon={<IconCalendar size="1.2rem" />}
          label="납기일"
          valueFormat="YYYY-MM-DD"
          {...form.getInputProps("deliveryDeadline")}
        />
        <Create.ButtonBox>
          <Create.Button color="gray" onClick={closeModal}>
            취소
          </Create.Button>
          <Create.Button
            disabled={validateFormValues({
              pageType: "purchaseOrder",
              values: form.values,
            })}
            onClick={onSubmit}
          >
            {userCode === "FLOUR" ? "수주 생성" : "발주 생성"}
          </Create.Button>
        </Create.ButtonBox>
      </Create.Wrapper>
    </>
  );
};
